import { useMemo } from 'react';

import { Box, ClassNameMap, Divider } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { useElementInView, useL10n, useUpBreakpoint } from '@uplift/hooks';
import { useProviderPublicMatchingProfile, useStates } from '@uplift/queries';
import { DateObject } from '@uplift/services';
import { ProviderMedicalRole, UserRole } from '@uplift/types';
import { useSchedulerSlots } from '@uplift/ui-v2';

import { Loading } from '../../loading/loading.component';
import {
  AcceptedInsurances, Scheduler, ScheduleSessionFooter, SelectedProviderJumpNav
} from './components';
import { ProviderHeader } from './components/ProviderHeader.component';
import { convertPublicProfileToSelectedProviderType } from './helpers';
import { AboutMeSection, AboutMeSectionProps, ExpertiseSection, ExpertiseSectionProps, MoreInfoSection } from './sections';
import { SelectedProviderContext, SelectedProviderContextProps } from './selected-provider.context';
import { useSelectedProviderLayoutStyles } from './styles';
import { SelectedProviderType } from './types';

const PSYCHIATRIST_REFERRAL_DAY_LOAD_LIMIT = 45;
const PSYCHIATRIST_DAY_LOAD_LIMIT = 30;
const THERAPIST_DAY_LOAD_LIMIT = 14;

type SelectedProviderContentProps = {
  stateId: string;
  patientId?: string;
  selectedProvider: SelectedProviderType;
  matchedInsuranceCompany?: SelectedProviderContextProps['matchedInsuranceCompany'];
  medicalRole?: ProviderMedicalRole;
  onScheduleSession?: (selectedSlot: DateObject) => void;
  excludeProviderIds?: string[];
  appointment?: {
    providerUuid: string;
    patientUuid: string;
    fromDatetime: DateObject;
    toDatetime: DateObject;
  };
  /**
   * this is used to highlight the specialties the user has selected during matching
   */
  selectedSpecialtyIds?: ExpertiseSectionProps['selectedSpecialtyIds'];

  /**
   * this is used to highlight the communities the user has selected during matching
   */
  selectedExperienceIds?: ExpertiseSectionProps['selectedExperienceIds'];

  /**
   * this is used to highlight the identities the user has selected during matching
   */
  selectedIdentityIds?: AboutMeSectionProps['selectedIdentityIds'];

  /**
   * this is based on the layout used to wrap this component and is used to determine the top positioning
   * on sticky elements (if true, top: 0px, if false, top: 64px)
   */
  isScrollableView?: boolean;

  appBarOffset?: number;
  /**
   * used for care-team profile view (as the scheduler isn't used) - will disable scheduler rendering,
   * though it's container will remain
   */
  hideScheduler?: boolean;
  /**
   * marks this instance as being used for public profile.
   *
   * - disables payment label on the scheduler
   * - shows service type chips under provider name
   */
  isPublicProfile?: boolean;

  /**
   * marks this instance as being used for provider matching profile preview.
   *
   * - disables schedule session button and timezone select on the scheduler
   */
  isPreview?: boolean;

  hideAcceptedInsurances?: boolean;
  mainContainerWidth?: string;

  forceMobile?: boolean;

  classes?: Partial<ClassNameMap<'root' | 'mainContainer'>>;
};

const SelectedProviderContent: React.VFC<SelectedProviderContentProps> = ({
  selectedProvider,
  matchedInsuranceCompany,
  appointment,
  onScheduleSession,
  isScrollableView = false,
  hideScheduler = false,
  isPublicProfile = false,
  isPreview = false,
  appBarOffset,
  mainContainerWidth,
  forceMobile = false,
  ...props
}) => {
  const isMdUp = useUpBreakpoint('md');
  const me = useCurrentuser();
  const l10n = useL10n();
  const { classes: layoutClasses } = useSelectedProviderLayoutStyles({
    classes: props.classes,
    isScrollableView,
    hideScheduler,
    appBarOffset,
    mainContainerWidth,
    forceMobile,
  }, {
    props: {
      classes: props.classes,
      isScrollableView,
      hideScheduler,
      appBarOffset,
      mainContainerWidth,
      forceMobile,
    }
  });
  const { inView: schedulerInView, elementRef } = useElementInView<HTMLDivElement>({ options: {}, initialViewState: true });
  const showScheduleSessionFooter = !schedulerInView && !hideScheduler;
  const daysAheadLimit = useMemo(() => {
    if (
      me.role === UserRole.provider
      && selectedProvider.medicalRole === ProviderMedicalRole.psychiatrist
    ) {
      return PSYCHIATRIST_REFERRAL_DAY_LOAD_LIMIT;
    }
    return selectedProvider.medicalRole === ProviderMedicalRole.psychiatrist
      ? PSYCHIATRIST_DAY_LOAD_LIMIT
      : THERAPIST_DAY_LOAD_LIMIT;
  }, [ me.role, selectedProvider.medicalRole ]);

  const patientId = (isPreview || selectedProvider.id === me?.id) ? undefined : (props.patientId || me?.id);
  const { slots } = useSchedulerSlots({
    providerId: selectedProvider.id,
    daysAheadLimit: daysAheadLimit,
    patientId: patientId,
    excludeProviderIds: props.excludeProviderIds,
    enabled: !hideScheduler,
  });
  const nextAvailableSlot = slots?.[0];

  const scheduler = useMemo(() => {
    if (hideScheduler || !onScheduleSession) {
      return null;
    }

    const PSYCHIATRIST_BOOKING_HEADER = l10n.matching.selectedProvider.bookYourSession;
    const THERAPIST_BOOKING_HEADER = l10n.matching.selectedProvider.bookYourWeeklySession;

    return (
      <Scheduler
        patientId={patientId}
        daysAheadLimit={daysAheadLimit}
        medicalRole={
          selectedProvider.medicalRole === ProviderMedicalRole.psychiatrist
            ? PSYCHIATRIST_BOOKING_HEADER
            : THERAPIST_BOOKING_HEADER
        }
        providerId={selectedProvider.id}
        onScheduleSession={onScheduleSession}
        appointment={appointment}
        excludeProviderIds={props.excludeProviderIds}
        isPreview={isPreview}
        forceMobile={forceMobile}
        userRole={isPublicProfile ? UserRole.patient : undefined}
      />
    );
  }, [ appointment, daysAheadLimit, forceMobile, hideScheduler, isPreview, l10n.matching.selectedProvider.bookYourSession, l10n.matching.selectedProvider.bookYourWeeklySession, onScheduleSession, props.excludeProviderIds, selectedProvider.id, selectedProvider.medicalRole, patientId, isPublicProfile ]);

  return (
    <SelectedProviderContext.Provider value={{ selectedProvider, matchedInsuranceCompany }}>
      <Box className={layoutClasses.root}>
        {!forceMobile && isMdUp && (
          <Box className={layoutClasses.schedulerContainer}>
            <Box className={layoutClasses.stickyScheduler}>
              {scheduler}
            </Box>
          </Box>
        )}


        <Box className={layoutClasses.fullBgWhite}>
          <Box className={layoutClasses.mainContainer}>
            <Box className={layoutClasses.contentContainer}>

              <Box className={layoutClasses.headerContainer}>
                <ProviderHeader forceMobile={forceMobile} stateId={props.stateId} />
                {!props.hideAcceptedInsurances && <AcceptedInsurances isPublicProfile={isPublicProfile} />}
              </Box>
              <div ref={elementRef}>
                {(forceMobile || !isMdUp) && (
                  <Box mb={forceMobile ? 0 : { xs: 0, sm: 4, md: 0 }}>
                    {scheduler}
                  </Box>
                )}
              </div>
              <Divider className={layoutClasses.headerBottomDivider} />
            </Box>
          </Box>
        </Box>

        <Box className={layoutClasses.stickyTabs}>
          <Box className={layoutClasses.mainContainer}>
            <Box className={layoutClasses.contentContainer}>
              <SelectedProviderJumpNav forceMobile={forceMobile} />
            </Box>
          </Box>
        </Box>


        <Box className={layoutClasses.mainContainer}>
          <Box display="flex" flexDirection="column" gap="2.5rem" py="2.5rem" className={layoutClasses.contentContainer}>
            <ExpertiseSection
              selectedSpecialtyIds={props.selectedSpecialtyIds}
              selectedExperienceIds={props.selectedExperienceIds}
              forceMobile={forceMobile}
            />
            <AboutMeSection
              selectedIdentityIds={props.selectedIdentityIds}
              forceMobile={forceMobile}
            />
            <MoreInfoSection forceMobile={forceMobile} />
          </Box>
        </Box>

        {showScheduleSessionFooter && (
          <ScheduleSessionFooter
            onClick={() => elementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            nextAvailable={nextAvailableSlot}
          />
        )}

      </Box>
    </SelectedProviderContext.Provider>
  );
};

type SelectedProviderWithQueryProps = {
  selectedProvider: string;
} & Omit<SelectedProviderContentProps, 'selectedProvider'>;

const SelectedProviderWithQuery: React.VFC<SelectedProviderWithQueryProps> = (props) => {
  const { states } = useStates();
  const { data: publicProfile, isInitialLoading } = useProviderPublicMatchingProfile(props.selectedProvider);

  if (isInitialLoading || !publicProfile) {
    return <Loading />;
  }

  const provider = convertPublicProfileToSelectedProviderType(publicProfile, states);

  return <SelectedProviderContent {...props} selectedProvider={provider} />;
};

export type SelectedProviderProps = {
  selectedProvider: string | SelectedProviderType;
} & Omit<SelectedProviderContentProps, 'selectedProvider'>;

export const SelectedProvider: React.VFC<SelectedProviderProps> = ({ selectedProvider, ...props }) => {
  if (typeof selectedProvider === 'string') {
    return <SelectedProviderWithQuery selectedProvider={selectedProvider} {...props} />;
  }

  return <SelectedProviderContent selectedProvider={selectedProvider} {...props} />;
};
